import { useContext, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { UserdataContext } from '../../../../App'
import { Job, listJobs } from '../../../../insfrastructure/services/jobs'
import { ListResponse } from '../../../../insfrastructure/services/utils'
import { formatDate } from '../../../../insfrastructure/utils/date-formatter'
import { StaticValues } from '../../../../insfrastructure/services/static_values'
import { TokenParams } from '../../../../insfrastructure/services/auth'
import { parseJwt } from '../../../../insfrastructure/utils/JWTParser'
import { getAuthToken } from '../../../../insfrastructure/services/token-manager'
import infoIcon from '../../../../assets/icons/info.png'
import searchIcon from '../../../../assets/icons/search.png'
import filterIcon from '../../../../assets/icons/filter.png'
import storageIcon from '../../../../assets/icons/storage.png'
import plusIcon from '../../../../assets/icons/plus.png'
import editIcon from '../../../../assets/icons/edit.png'
import peopleIcon from '../../../../assets/icons/people.png'
import duplicateIcon from '../../../../assets/icons/duplicate.png'
import deleteIcon from '../../../../assets/icons/delete.svg'
import openedEyeIcon from '../../../../assets/icons/opened_eye.svg'
import Table from '../../../components/table'
import JobDeleteModal from './job-delete-modal'
import { logger } from '../../../../insfrastructure/services/analytics'
import './styles.sass'

// Constantes de status de vagas
const JOB_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  CANCELLED: 'cancelled',
  IN_ANALYSIS: 'in_analysis',
  // Backend separou o status "hired" da vaga do status "hired" da
  // candidatura (colidiam por acidente) — vaga preenchida agora é
  // "filled". Chave HIRED mantida para não precisar renomear os
  // consumidores; só o valor mudou.
  HIRED: 'filled'
} as const;

// Função auxiliar para verificar se deve mostrar a lista de candidatos
const canShowCandidatesList = (status: string): boolean => {
  return status !== JOB_STATUS.CANCELLED && status !== JOB_STATUS.INACTIVE;
};

// Função para calcular a porcentagem de empregabilidade de forma segura
const calculateEmployabilityPercentage = (loading: boolean, hiredCount: number, totalCount: number): number => {
  // Se estiver carregando, retorna 0
  if (loading) {
    return 0;
  }
  
  // Se não houver vagas, retorna 0
  if (!totalCount || totalCount === 0) {
    return 0;
  }
  
  // Calcula a porcentagem
  const percentage = (hiredCount / totalCount) * 100;
  
  // Garante que o valor é um número válido
  if (!isFinite(percentage) || isNaN(percentage)) {
    return 0;
  }
  
  // Arredonda para 2 casas decimais
  return Math.round(percentage * 100) / 100;
}

/**
 * Aplica filtros de perfil aos parâmetros de busca de vagas
 * 
 * - em_job_manager: Backend filtra automaticamente por escola_do_mecanico (não precisa enviar parâmetros)
 * - job_manager: Frontend envia managerId para o backend filtrar
 */
const applyProfileFilters = (
  params: Record<string, any>,
  hiredJobsParams: Record<string, any>,
  parsedJWT?: TokenParams
): void => {
  if (!parsedJWT?.profiles) {
    return;
  }

  const hasEmJobManager = parsedJWT.profiles.includes('em_job_manager');
  const hasJobManager = parsedJWT.profiles.includes('job_manager');

  // Prioridade: em_job_manager tem precedência sobre job_manager
  // Se tem em_job_manager, o backend filtra automaticamente por escola_do_mecanico
  // Não é necessário adicionar parâmetros adicionais
  if (hasEmJobManager) {
    return;
  }

  // Se é apenas job_manager (sem em_job_manager), usar managerId
  if (hasJobManager && parsedJWT.manager_id) {
    params.managerId = parsedJWT.manager_id;
    hiredJobsParams.managerId = parsedJWT.manager_id;
  }
}

const JobOffersPanel = () => {
  const {userData} = useContext<any>(UserdataContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const [offers, setOffers] = useState<ListResponse<Job>>({
    results: [],
    count: 0
  })
  const [hiredOffersCount, setHiredOffersCount] = useState(0)
  const [cancelledOffersCount, setCancelledOffersCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [jobStatus, setJobStatus] = useState<Record<string, any>>({})
  const [parsedJWT, setParsedJWT] = useState<TokenParams>()
  const [selectedItemToDelete, setSelectedItemToDelete] = useState<any | null>(null)

  useEffect(() => {
    onParseJWT();
  }, [])
  

  useEffect(() => {
    if (!parsedJWT) {
      return;
    };

    listOffers({ 
      offset: searchParams.get('page') ? (parseInt(searchParams.get('page')+'') - 1) * 10 : 0,
      search: searchParams.get('search')
    });
    getStaticValues();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedJWT, searchParams])

  const onParseJWT = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;
      
      const parsedJWT: TokenParams = parseJwt(authToken);
      
      setParsedJWT(parsedJWT);
    } catch (error) {}
  }

  const listOffers = async ({offset, search}: Record<string, any>) => {
    logger.info('Buscando lista de vagas no painel', {
      offset,
      search,
      category: 'job_offers_panel'
    });
    
    try {
      setLoading(true);
      setOffers({
        results: [],
        count: 0
      });

      let params: any = { offset, limit: 10 };
      let hiredJobsParams: any = { status: JOB_STATUS.HIRED.toUpperCase(), offset: 0, limit: 1 };
      let cancelledJobsParams: any = { status: JOB_STATUS.CANCELLED.toUpperCase(), offset: 0, limit: 1 };
      let urlParams: any = {page: (offset / 10) + 1 + ''};

      if (search) {
        params.search = search;
        urlParams.search = search;
      }

      // Aplica filtros baseados no perfil do usuário
      applyProfileFilters(params, hiredJobsParams, parsedJWT);
      // Vaga cancelada não é pública: só conta com o escopo do dono (managerId).
      if (params.managerId) cancelledJobsParams.managerId = params.managerId;

      const offersResults = await listJobs(params);

      if (!offersResults?.data) {
        throw new Error('Resposta da API de vagas não contém dados');
      }

      // TODO: Backend retorna status em UPPERCASE, converter para lowercase
      // até que o backend seja atualizado para retornar no formato correto
      offersResults.data.forEach((x: any) => {
        try {
          x.status = x.status?.toLowerCase() || '';
        } catch (statusError) {
          logger.warn('Erro ao converter status da vaga para lowercase', {
            jobpost_id: x.jobpostId,
            status: x.status,
            category: 'job_offers_panel'
          });
          x.status = '';
        }
      });

      setOffers({
        results: offersResults.data,
        count: offersResults.totalCount || 0
      });
      
      try {
        const hiredResults = await listJobs(hiredJobsParams);
        setHiredOffersCount(hiredResults.totalCount || 0);
      } catch (hiredError) {
        logger.error('Erro ao buscar contagem de vagas preenchidas', hiredError as Error, {
          category: 'job_offers_panel'
        });
        setHiredOffersCount(0);
      }

      try {
        const cancelledResults = await listJobs(cancelledJobsParams);
        setCancelledOffersCount(cancelledResults.totalCount || 0);
      } catch (cancelledError) {
        logger.error('Erro ao buscar contagem de vagas canceladas', cancelledError as Error, {
          category: 'job_offers_panel'
        });
        setCancelledOffersCount(0);
      }

      setOffset(offset);
      setSearch(search);
      setSearchParams(urlParams);
      
      logger.info('Lista de vagas carregada com sucesso', {
        total_count: offersResults.totalCount,
        results_count: offersResults.data.length,
        category: 'job_offers_panel'
      });
      
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (error) {
      logger.error('Erro ao buscar lista de vagas no painel', error as Error, {
        offset,
        search,
        category: 'job_offers_panel',
        error_type: error instanceof Error ? error.constructor.name : typeof error
      });
      
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }

  const getStaticValues = async () => {
    try {  
      setTimeout(() => {
        const staticValues = JSON.parse(localStorage.getItem('static_values')+'') as StaticValues;
        const job_status = staticValues.job_status;
        
        setJobStatus(job_status);
      }, 1000);
    } catch (error) {}
  }

  const onSearch = async () => {
    listOffers({ offset: 0, search });
  }

  const onDeleteJob = async () => {
    setSelectedItemToDelete(null);
    listOffers({ 
      offset: searchParams.get('page') ? (parseInt(searchParams.get('page')+'') - 1) * 10 : 0,
      search: searchParams.get('search')
    });
  }

  return (
      <div className='job-offers company-page'>
        <div className='block'>
            <div className='block-header'>
              <div className='title'>
                <img src={storageIcon} alt='offers' />
                <h3>Minhas vagas</h3>
              </div>
              {userData?.usertype === 'job_manager' &&
              <Link to='/offers/create'>
                <button className='primary add-btn'>
                  <img src={plusIcon} alt='add-offer' />
                  <span>Criar vaga</span>
                </button>
              </Link>}
            </div>
            {
              userData?.usertype === 'job_manager' &&
              <div className='general-vision'>
                <div className='data'>
                  <h3>Visão Geral</h3>
                  <h4>Total de vagas: <b>{ Math.max(0, offers.count - cancelledOffersCount) }</b></h4>
                  <h4>Vagas preenchidas: <b>{ hiredOffersCount }</b></h4>
                </div>
                <div className='graph-container'>
                  <CircularChart percentage={calculateEmployabilityPercentage(loading, hiredOffersCount, Math.max(0, offers.count - cancelledOffersCount))} />
                  <h4>Empregabilidade</h4>
                </div>
              </div>
            }
            <div className='oportunities-searcher'>
              <img className='search-icon' src={searchIcon} alt='search' onClick={onSearch} />
              <input type='text' placeholder='Pesquisar vaga' value={search} onChange={e => setSearch(e.target.value)} />
              <img className='filter-icon' src={filterIcon} alt='filter' />
            </div>
            <div className='offers'>
                <div className='job-results'>
                  <Table
                  data={offers.results}
                  total={offers.count}
                  limit={10}
                  offset={offset}
                  loading={loading}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  onNavigate={(params: any) => {
                    listOffers({ offset: params.offset, search });
                  }}
                  header={
                  <thead>
                    <tr>
                      <th className='id'>ID:</th>
                      <th className='company'>Empresa:</th>
                      <th className='name'>Nome da vaga:</th>
                      <th>Data de publicação:</th>
                      <th>Status:</th>
                      {userData?.usertype === 'job_manager' && <th>Candidatos:</th>}
                      {userData?.usertype === 'job_manager' && <th className='see-candidates'>Ver candidatos</th>}
                      <th className='actions'>
                        <div className="tooltip">
                          <div>
                            <span>Acessar vaga</span>
                            <img src={infoIcon} alt='info-icon' />
                          </div>
                          <span className="tooltiptext">Só é possivel editar vagas sem candidaturas. Para editar, exclua esta vaga e crie uma nova com a informação correta.</span>
                        </div>
                      </th>
                      {userData?.usertype === 'job_manager' && <th>Excluir vaga</th>}
                      {userData?.usertype === 'job_manager' && <th>Duplicar:</th>}
                    </tr>
                  </thead>}
                  body={
                    (offers: any) => {
                      return offers.map((offer: any, index: number) =>
                      <tr key={'tr_'+ (offer.jobpostId ?? index)}>
                        <td className='id'>{offer.jobpostId}</td>
                        <td className='company' title={offer.company?.name}>{offer.company?.name}</td>
                        <td className='name' title={offer.jobTitle['name']}>{offer.jobTitle['name']}</td>
                        <td>{formatDate(offer.publishDate)}</td>
                        <td>
                          <span className={
                            'status '+offer.status
                          }>{ jobStatus[offer.status] }</span>
                        </td>
                        {userData?.usertype === 'job_manager' && (
                          <td>
                            {(() => {
                              try {
                                const count = offer.candidatesCount;
                                if (count === undefined || count === null) {
                                  logger.warn('Contagem de candidatos não disponível para vaga', {
                                    jobpost_id: offer.jobpostId,
                                    category: 'job_offers_panel'
                                  });
                                  return '-';
                                }
                                return count;
                              } catch (error) {
                                logger.error('Erro ao exibir contagem de candidatos', error as Error, {
                                  jobpost_id: offer.jobpostId,
                                  category: 'job_offers_panel'
                                });
                                return '-';
                              }
                            })()}
                          </td>
                        )}
                        {userData?.usertype === 'job_manager' &&
                        <td>
                          {canShowCandidatesList(offer.status) &&
                          <Link 
                            to={'/offers/'+offer.jobpostId+'/candidates'}
                            onClick={() => {
                              logger.info('Usuário clicou para ver candidatos de uma vaga', {
                                jobpost_id: offer.jobpostId,
                                job_title: offer.jobTitle?.name,
                                status: offer.status,
                                candidates_count: offer.candidatesCount,
                                category: 'job_offers_panel'
                              });
                            }}
                          >
                            <img className='people-icon' alt='candidates' src={peopleIcon} />
                          </Link>}
                        </td>}
                        <td>
                          {userData?.usertype === 'candidate' && 
                          <Link to={'/offers/'+offer.jobpostId+'/details'}>
                            <img className='edit-icon' alt='edit' src={editIcon} />
                          </Link>}
                          {userData?.usertype === 'job_manager' && offer.status === JOB_STATUS.ACTIVE && offer.candidatesCount === 0 &&
                          <Link to={'/offers/'+offer.jobpostId}>
                            <img className='edit-icon' alt='edit' src={editIcon} />
                          </Link>}
                          {userData?.usertype === 'job_manager' && (offer.status !== JOB_STATUS.ACTIVE || offer.candidatesCount > 0) &&
                          <Link to={'/offers/'+offer.jobpostId+'/details'}>
                            <img className='edit-icon' alt='see' src={openedEyeIcon} />
                          </Link>}
                        </td>
                        <td>
                            {(() => {
                              if (userData?.usertype !== 'job_manager' || offer.status !== 'active') {
                                return null;
                              }

                              const authToken = getAuthToken();
                              const parsedJWT = authToken ? JSON.parse(atob(authToken.split('.')[1] || '{}')) : {};

                              // Se for em_job_manager: só aparece se a escola do usuário for igual ao jobpostEmId
                              if (parsedJWT?.profiles?.includes('em_job_manager')) {
                                const userEmUnitId = parsedJWT?.em_unit_id;
                                const jobpostEmId = offer.jobpostEmId;

                                if (userEmUnitId && jobpostEmId && userEmUnitId.toString() === jobpostEmId.toString()) {
                                  return <img className='delete-icon' alt='delete' src={deleteIcon} onClick={() => setSelectedItemToDelete(offer)} />
                                }

                                return null;
                              }

                              // Se for apenas job_manager (sem em_job_manager): sempre aparece
                              if (parsedJWT?.profiles?.includes('job_manager') && !parsedJWT?.profiles?.includes('em_job_manager')) {
                                return <img className='delete-icon' alt='delete' src={deleteIcon} onClick={() => setSelectedItemToDelete(offer)} />
                              }

                              return null;
                            })()}
                        </td>
                        {userData?.usertype === 'job_manager' && (
                        <td>
                          {offer.status !== JOB_STATUS.ACTIVE && (
                            <Link to={'/offers/create'} state={{ offer_id: offer.jobpostId }}>
                              <img className='duplicate-icon' alt='duplicate' src={duplicateIcon} />
                            </Link>
                          )}
                        </td>
                        )}
                      </tr>
                      )
                    }
                  }/>
                </div>
            </div>
        </div>
        <JobDeleteModal
        jobpost={selectedItemToDelete}
        render={!!selectedItemToDelete}
        onSubmitted={onDeleteJob}
        onClose={() => setSelectedItemToDelete(null)}/>
      </div>
  )
}

export default JobOffersPanel;

export const CircularChart = ({ percentage }: { percentage: number }) => {
  // Garante que a porcentagem é sempre um número válido entre 0 e 100
  const safePercentage = !isFinite(percentage) || isNaN(percentage) 
    ? 0 
    : Math.max(0, Math.min(100, percentage));
  
  // Formata para exibição (sem casas decimais se for inteiro)
  const displayPercentage = safePercentage % 1 === 0 
    ? Math.round(safePercentage) 
    : safePercentage.toFixed(1);

  return (
    <svg width={100} height={100} viewBox="0 0 36 36">
      <path className="circle-bg" style={{
         fill: 'none',
         stroke: '#E6E6E6',
         strokeWidth: 2.5
      }}
        d="M18 2.0845
          a 15.9155 15.9155 0 0 1 0 31.831
          a 15.9155 15.9155 0 0 1 0 -31.831"
      />
      <path
        d="M18 2.0845
          a 15.9155 15.9155 0 0 1 0 31.831
          a 15.9155 15.9155 0 0 1 0 -31.831"
        fill="none"
        stroke="#9FD67B"
        strokeWidth="2.5"
        strokeDasharray={`${safePercentage}, 100`}
        style={{ transition: '0.5s' }}
      />
      <text x="50%" y="50%" textAnchor="middle" fill="black" fontSize="8px" dy=".3em">{displayPercentage}%</text>
    </svg>
  )
}
